import { Alert, AlertDescription, AlertIcon, Box, Button, IconButton, Spinner, Text } from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { isEmpty } from "lodash";
import { FunctionComponent, ReactElement, useState } from "react";
import { Link, generatePath, useSearchParams } from "react-router-dom";

// Components
import DataTable from "../../../shared/DataTable/DataTable";
import LinkableTableCell from "../../../shared/DataTable/shared/LinkableTableCell";
import LayoutWrapper from "../../../shared/layout";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import useClaimsToast from "../../../hooks/useToast";
import { IParams, getCacheKeyObject } from "../../../shared/DataTable/hooks/useSearchParams";

// Utils
import { RouterPathMapping } from "../../../Router";
import { getCasesList } from "../../../utils/api/cases";
import { dummyPaginatedApiResponse } from "../../../utils/consts";
import { convertUnixTimestampToLocalDate, createAttorneyUpdateLinkFromEnv } from "../../../utils/helpers";
import { DataTableColumn, KVP, PaginatedApiResponse, PaginationResponse, SortDirection } from "../../../utils/types";

// Protos
import { CaseListing, CasesListResponse } from "../../../utils/api/protos/messages/cases.message_pb";
import { PaginationResponse as ApiPaginationResponse } from "../../../utils/api/protos/messages/core.message_pb";
import { getProvider } from "../../../utils/api/providers";
import { Provider } from "../../../utils/api/protos/messages/providers.message_pb";
import { ExternalLinkIcon } from "@chakra-ui/icons";

interface AttorneyCasesPageProps {}

/**
 * Define columns to show in the DataTable
 */
const columns: Array<DataTableColumn> = [
	{
		header: "PASS Case Id",
		id: "id",
		filterable: true,
		minWidth: "13ch",
		isHidden: false,
		isSelectable: true,
		linkable: true,
		isSortable: true,
		allData: true,
		valueMap: (row: KVP) => (
			<LinkableTableCell
				To={generatePath(RouterPathMapping.Attorney_CaseDetail, { id: row["id"]?.toString() })}
				Contents={row["id"]}
			/>
		)
	},
	{
		header: "LAMP Case ID",
		id: "lampCaseId",
		filterable: true,
		minWidth: "15ch",
		isHidden: false,
		isSortable: true
	},
	{
		header: "Member",
		id: "clientName",
		filterable: true,
		minWidth: "10ch",
		isHidden: false,
		isSortable: true
	},
	{
		header: "Member ID",
		id: "memberId",
		filterable: true,
		minWidth: "10ch",
		isHidden: false,
		isSortable: true
	},
	{
		header: "Group",
		id: "clientGroupId",
		filterable: true,
		width: "15ch",
		minWidth: "15ch",
		truncate: true,
		isHidden: false,
		isSortable: true
	},
	{
		header: "State",
		id: "clientState",
		filterable: true,
		width: "8ch",
		minWidth: "8ch",
		isHidden: false,
		isSortable: true
	},
	{
		header: "Submitted",
		id: "intakeDate",
		filterable: false,
		minWidth: "10ch",
		isHidden: false,
		isSortable: true,
		isDate: true
	},
	{
		header: "Accepted",
		id: "referralAcceptedDate",
		filterable: false,
		minWidth: "10ch",
		isHidden: false,
		isSortable: true,
		isDate: true
	},
	{
		header: "",
		id: "actions",
		filterable: false,
		minWidth: "10ch",
		isHidden: false,
		isSelectable: false,
		linkable: false,
		isSortable: false,
		allData: true,
		valueMap: (row: KVP) => {
			// Check if row exists first
			if (!row) return <></>;

			// Get the ID, which should always be a string based on formatCasesForTable
			const caseId = row.id;

			// Don't render button if we don't have a valid caseId
			if (caseId === undefined || caseId === null) return <></>;

			return (
				<Button
					as={Link}
					to={generatePath(RouterPathMapping.Attorney_CaseDetail, { id: caseId.toString() })}
					marginRight={4}
					size="sm"
					minWidth="74px"
					colorScheme="orange"
					padding={2}>
					View
				</Button>
			);
		}
	}
];

/**
 * Handles the Cases listing page for a single attorney
 */
const AttorneyCasesPage: FunctionComponent<AttorneyCasesPageProps> = (props: AttorneyCasesPageProps): ReactElement => {
	// State
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isProviderFetchLoading, setIsProviderFetchLoading] = useState<boolean>(false);
	const [isBannerVisible, setIsBannerVisible] = useState<boolean>(true);
	const [casesData, setCasesData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [localCacheKey, setLocalCacheKey] = useState<string>("");

	// Hooks
	const { getUser } = useAuth();
	const [searchParams] = useSearchParams();
	const user = getUser();
	const toast = useClaimsToast();

	// Default parameters for the DataTable
	const defaultCasesParams: Partial<IParams> = {
		sortDirection: SortDirection.Desc,
		sortField: "intakeDate"
	};

	/**
	 * Helper function to allow for child objects to trigger a fetch of Cases data
	 * with the active tab's cacheKey
	 * @param cacheKey The cacheKey of the currently active table
	 */
	const handleFetchCases = (cacheKey: string): void => {
		if (!isEmpty(cacheKey)) {
			setIsLoading(true);
			if (cacheKey !== localCacheKey) setLocalCacheKey(cacheKey);
			getCasesList(getCacheKeyObject(cacheKey), parseInt(user?.externalId ?? "0"))
				.then((response: CasesListResponse) => {
					// Build response data
					const returnedCasesData: Array<{ [key: string]: string | number }> = response
						.getCasesList()
						.map((apiCase: CaseListing) => ({
							id: apiCase.getCaseid(),
							lampCaseId: apiCase.getLampcaseid() ?? "N/A",
							clientName: apiCase.getClientname(),
							attyId: apiCase.getAttyid(),
							memberId: apiCase.getMemberid(),
							providerName: apiCase.getProvidername(),
							clientGroupId: apiCase.getClientgroupid() || "N/A",
							clientState: apiCase.getClientstate() || "",
							intakeDate: apiCase.getIntakedate()
								? convertUnixTimestampToLocalDate(apiCase.getIntakedate()).toLocaleString()
								: "",
							referralAcceptedDate: apiCase.getReferralaccepteddate()
								? convertUnixTimestampToLocalDate(apiCase.getReferralaccepteddate()).toLocaleString()
								: ""
						}));
					const foundPaginationData: ApiPaginationResponse | undefined = response.getPaginationResponse();
					if (foundPaginationData) {
						const returnedPaginationData: PaginationResponse = {
							current_page: foundPaginationData.getCurrentPage(),
							first_page: foundPaginationData.getFirstPage(),
							last_page: foundPaginationData.getLastPage(),
							perPage: foundPaginationData.getLimit(),
							total: foundPaginationData.getTotal()
						};

						setCasesData({
							data: returnedCasesData,
							meta: returnedPaginationData
						});
					} else {
						toast("Error", "There was a problem deciphering the pagination metadata", "error");
						console.error("Attempting to parse pagination metadata that does not exist!!");
					}
				})
				.catch((err) => {
					toast("Error", (err as RpcError).message, "error");
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const openLampAttorneyUpdateForm = async () => {
		setIsProviderFetchLoading(true);

		getProvider(+(user?.externalId ?? "0"))
			.then((response) => {
				const foundProvider: Provider | undefined = response.getProvider();
				if (foundProvider) {
					console.log("Found provider " + JSON.stringify(foundProvider.toObject()));
					const attorneyUpdateLink = createAttorneyUpdateLinkFromEnv(
						foundProvider.getAttyid(),
						foundProvider.getUpdateInfoToken()
					);
					// _blank opens the URL in a new tab
					// noopener and noreferrer are critical for security
					window.open(attorneyUpdateLink, "_blank", "noopener,noreferrer");
				} else {
					toast("Error", "Couldn't navigate to LAMP™, please contact customer service", "error");
				}
			})
			.catch((err: RpcError) => {
				console.error(err);
				toast("Error", "Couldn't navigate to LAMP™, please contact customer service", "error");
			})
			.finally(() => {
				setIsProviderFetchLoading(false);
			});
	};

	return (
		<>
			<LayoutWrapper>
				{isBannerVisible && (
					<Box mx={10} mt={10} mb={4}>
						<Alert status="warning" bg="orange.500" color="white" alignItems="flex-start" px={6} py={3}>
							<AlertIcon color="white" mt={1} />
							<AlertDescription flex={1}>
								<Text>
									If your case does not appear in the Claims System, please Generate Claim Forms in the LAMP from the
									link in the email sent to you during acceptance of the case. If your case still does not appear,
									please reach out to{" "}
									<Text as="span" fontWeight="bold">
										providerInformation@legaleaseplan.com
									</Text>
									.
								</Text>
							</AlertDescription>
							<Button
								colorScheme="orange"
								variant="outline"
								backgroundColor="white"
								color="orange.700"
								mx="5"
								border="none"
								opacity="0.9"
								onClick={openLampAttorneyUpdateForm}
								_hover={{ opacity: 1 }}>
								{isProviderFetchLoading ? <Spinner size="sm" mx="3px" /> : <ExternalLinkIcon mx="3px" mb="2px" />}
								&nbsp;Go To LAMP™
							</Button>
							<IconButton
								aria-label="Dismiss notification"
								icon={
									<Text fontSize="lg" lineHeight={1}>
										✕
									</Text>
								}
								variant="ghost"
								colorScheme="whiteAlpha"
								size="sm"
								mt={0.5}
								minW="auto"
								onClick={() => setIsBannerVisible(false)}
							/>
						</Alert>
					</Box>
				)}
				<Box mx={10} mt={isBannerVisible ? 0 : 10} mb={10}>
					<DataTable
						data={casesData}
						columns={columns}
						refetchTrigger={handleFetchCases}
						defaultParams={defaultCasesParams}
						existingParams={searchParams}
						shouldUpdateUrl={true}
						shouldShowSelection={false}
						shouldShowFilterToggle={false}
						shouldShowTableContainer={true}
						isLoading={isLoading}
					/>
				</Box>
			</LayoutWrapper>
		</>
	);
};

export default AttorneyCasesPage;
